import { useState, useMemo, useEffect } from 'react';
import { Container, Typography, Box, IconButton, CircularProgress, Alert, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField, Snackbar, Tooltip, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ArrowBack, Share, ContentCopy, Edit, FolderOpen, PictureAsPdf, Close, Delete, CleaningServices as ClearIcon } from '@mui/icons-material';
import { useNavigate, useParams, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useBrowseNavigation } from '../hooks/useBrowseNavigation';
import { BrowseNavigationBar } from '../components/BrowseNavigationBar';
import { usePermissions } from '../hooks/usePermissions';
import { useCatalogDescriptions } from '../hooks/useCatalogDescriptions';
import { useCatalogKeywords } from '../hooks/useCatalogKeywords';
import { useCatalogMeasurements } from '../hooks/useCatalogMeasurements';
import { MeasurementData, CatalogItem } from '../contexts/CatalogContext';

/**
 * Typed subset of description/public-route data used by the hero artist block.
 * `creators` matches the shape returned by /api/descriptions (authenticated) and
 * the mapped `creatorsList` from the public catalog endpoint.
 */
interface HeroCatalogData extends Record<string, unknown> {
  creators?: Array<{ name?: string; isCurrentUser?: boolean; notes?: string }>;
}
import { useCatalog } from '../hooks/useCatalog';
import {
  formatControlledVocabulary,
  formatSingleControlledVocabulary,
  formatCreationDate,
  formatCreationDateThumbnail,
  getCreatorNames,
  formatInscriptions,
  formatMedium as formatMediumHelper
} from '../utils/catalogFormatters';
import { useObjectImages, CatalogItemData } from '../hooks/useObjectImages';
import ImageGallery from '../components/ImageGallery';
import { NoImagePlaceholder } from '../components/NoImagePlaceholder';
import { ReusePlaceholder, DeletedPlaceholder } from '../components/placeholders';
import { getArtworkTitle, getThumbnailUrl, getDetailImageUrl, getPublicDetailImageUrl, getPublicCatalogItem, Artwork, PrivateArtworkError } from '../utils/artworkApi';
import { useAuth } from '../hooks/useAuth';
import { useActiveCatalog } from '../hooks/useActiveCatalog';
import { useUserProfile } from '../hooks/useUserProfile';
import { buildArtworkDetailLink } from '../utils/artworkDetailLink';
import { getCrossOrigin } from '../utils/imageUtils';
import { AddToCollectionDialog } from '../components/AddToCollectionDialog';
import { en } from '../translations/en';
import { generateCatalogPdf, type PdfExportItem, type PdfProgressCallback } from '../utils/pdfExport';
import { ExportDialog } from '../components/export/ExportDialog';
import { MarkDestroyedDialog } from '../components/MarkDestroyedDialog';
import { DeleteObjectDialog } from '../components/DeleteObjectDialog';
import { restoreCatalogItem, deleteCatalogItem, clearCatalogItem } from '../utils/catalogStatusApi';
import { logger } from '../utils/logger';
import { extractAxiosErrorMessage } from '../utils/errorUtils';
import { DescriptionSection } from '../components/object-details/DescriptionSection';
import { ExhibitionsPublicationsSection } from '../components/object-details/ExhibitionsPublicationsSection';
import { AwardsSection } from '../components/object-details/AwardsSection';
import { ProvenanceSection } from '../components/object-details/ProvenanceSection';
import { InternalOnlyDataSection } from '../components/object-details/InternalOnlyDataSection';
import { RelatedItemsSection } from '../components/object-details/RelatedItemsSection';
import { DocumentsSection } from '../components/DocumentsSection';
import { parseArrayField } from '../components/object-details/utils';

const catalogValueSx = {
  color: 'text.primary',
  fontSize: '0.95rem',
};

// Linear dimension types use the global unit; non-linear use their own
const LINEAR_DIMENSION_TYPES = new Set(['height', 'width', 'depth']);

/** Dimension label abbreviations for display */
const DIMENSION_LABELS: Record<string, string> = { height: 'H', width: 'W', depth: 'D' };

export const ObjectDetails = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { artworkId } = useParams<{ artworkId: string }>();
  const { isAuthenticated } = useAuth();
  const { activeCatalogOwnerId, activeDelegation, isDelegateOf } = useActiveCatalog();
  const { profile } = useUserProfile();
  const { permissions, canEditCatalog } = usePermissions();
  // GL#24: per-item route resolver for union-search prev/next nav.
  // Prevents 404 when navigating to an others'-public item (which must use
  // /public/object-details/) from an own item (which uses /object-details/).
  const currentUserId = profile?.userId ?? null;
  const browseNav = useBrowseNavigation({
    buildItemRoute: (ownerUserId, itemId) =>
      buildArtworkDetailLink(
        { artworkId: itemId, userId: ownerUserId },
        { currentUserId, isDelegateOf }
      ),
  });
  const isDelegate = permissions?.role === 'delegate';
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [catalogData, setCatalogData] = useState<HeroCatalogData | null>(null);
  const [keywordsData, setKeywordsData] = useState<Record<string, unknown> | null>(null);
  const [measurementData, setMeasurementData] = useState<MeasurementData | null>(null);
  const [addToCollectionDialog, setAddToCollectionDialog] = useState(false);
  const [publicArtwork, setPublicArtwork] = useState<Artwork | null>(null);
  const [publicLoading, setPublicLoading] = useState(false);
  const [publicError, setPublicError] = useState<string | null>(null);
  const [isPrivateArtwork, setIsPrivateArtwork] = useState(false);
  // Owner of a private artwork (only set when the public endpoint returned a
  // 403 with ownerId). Drives the "Check if you have access" recovery link so
  // delegates can re-enter the authenticated detail flow.
  const [privateArtworkOwnerId, setPrivateArtworkOwnerId] = useState<string | null>(null);
  const [catalogItemData, setCatalogItemData] = useState<CatalogItem | null>(null);
  const [itemLoading, setItemLoading] = useState(false);
  const [itemError, setItemError] = useState<string | null>(null);
  const [provenanceData, setProvenanceData] = useState<Array<Record<string, unknown>>>([]);
  const [additionalInfoData, setAdditionalInfoData] = useState<Record<string, unknown> | null>(null);

  const { getCatalogItem, getAdditionalInfo, getProvenanceChain } = useCatalog();

  const [searchParams] = useSearchParams();
  const shareUrl = window.location.href;
  const isPublicRoute = location.pathname.includes('/public/');
  const showPublicViewBanner = isPublicRoute && searchParams.get('from') === 'upload';

  // ``?owner=`` overrides ``activeCatalogOwnerId`` for the authenticated
  // detail fetch — set by search/bookmark links when the viewer is a delegate
  // of the artwork owner, and by the "Check if you have access" recovery
  // button after a private-artwork 403. Falling through to
  // ``activeCatalogOwnerId`` preserves the sidebar-driven flow.
  const ownerParam = searchParams.get('owner');
  const effectiveOwnerId = ownerParam || activeCatalogOwnerId;

  // Fetch public catalog item when on public route
  useEffect(() => {
    if (artworkId && isPublicRoute) {
      let cancelled = false;
      const fetchPublicItem = async () => {
        setPublicLoading(true);
        setPublicError(null);
        setIsPrivateArtwork(false);
        setPrivateArtworkOwnerId(null);
        try {
          const item = await getPublicCatalogItem(artworkId);
          if (!cancelled) {
            if (item) {
              setPublicArtwork(item);
            } else {
              setPublicError('Artwork not found');
            }
            setPublicLoading(false);
          }
        } catch (error) {
          if (!cancelled) {
            if (error instanceof PrivateArtworkError) {
              setIsPrivateArtwork(true);
              setPrivateArtworkOwnerId(error.ownerId ?? null);
            } else {
              logger.error('Failed to load public artwork', error);
              setPublicError('Failed to load artwork');
            }
            setPublicLoading(false);
          }
        }
      };
      fetchPublicItem();
      return () => { cancelled = true; };
    }
  }, [artworkId, isPublicRoute]);

  const handleShare = () => {
    setShareModalOpen(true);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setSnackbarMessage('Link copied to clipboard!');
    setSnackbarOpen(true);
    setShareModalOpen(false);
  };

  const [pdfExporting, setPdfExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearLoading, setClearLoading] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);

  const handleExportPdf = async (includePrivateFields: boolean, onProgress?: PdfProgressCallback) => {
    setPdfExporting(true);
    try {
      const aliNumber = (keywordsData?.aliCatalogNumber as string) || '';
      const pdfItem: PdfExportItem = {
        aliCatalogNumber: aliNumber,
        title: title || '',
        creators: catalogData?.creators
          ? formatControlledVocabulary(catalogData.creators as unknown[]) || ''
          : '',
        creationDate: catalogData?.creationDate
          ? formatCreationDate(catalogData.creationDate) || ''
          : '',
        workType: catalogData?.workType
          ? formatSingleControlledVocabulary(catalogData.workType) || ''
          : '',
        techniques: catalogData?.techniques
          ? formatControlledVocabulary(catalogData.techniques as unknown[]) || ''
          : '',
        materials: catalogData?.materials
          ? formatControlledVocabulary(catalogData.materials as unknown[]) || ''
          : '',
        editions: catalogData?.editions
          ? (catalogData.editions as Array<{ type: string; count: number; total: number }>)
              .map((e) => `${e.type}: ${e.count}/${e.total}`)
              .join('; ')
          : '',
        dimensions: formatHeroDimensionsLocal(measurementData) || '',
        keywords: (keywordsData?.keywords as string[])?.join(', ') || '',
        publicNotes: (additionalInfoData?.publicNotes as string) || '',
        privateNotes: (additionalInfoData?.privateNotes as string) || '',
        physicalLocation: (additionalInfoData?.physicalLocation as string) || '',
        estimatedValue: (additionalInfoData?.estimatedValue as string) || '',
        provenanceRecords: provenanceData
          .map((r) => (r.publicRendering as string) || '')
          .filter(Boolean)
          .join('; '),
        exhibitions: (() => {
          const items = (additionalInfoData?.exhibitions as Array<Record<string, unknown>>) || [];
          return items
            .filter((e) => e.type === 'exhibition')
            .map((e) => (e.exhibitionType as string) || '')
            .filter(Boolean)
            .join('; ');
        })(),
        publications: (() => {
          const items = (additionalInfoData?.exhibitions as Array<Record<string, unknown>>) || [];
          return items
            .filter((e) => e.type === 'publication')
            .map((e) => (e.publicationText as string) || '')
            .filter(Boolean)
            .join('; ');
        })(),
        awards: (() => {
          const items = (additionalInfoData?.exhibitions as Array<Record<string, unknown>>) || [];
          return items
            .filter((e) => e.type === 'award')
            .map((e) => (e.publicationText as string) || '')
            .filter(Boolean)
            .join('; ');
        })(),
        customCatalogNumbers: (keywordsData?.customCatalogNumbers as string[])?.join('; ') || '',
        alternativeTitles: catalogData?.alternativeTitles
          ? parseArrayField(catalogData.alternativeTitles)
          : '',
        substrate: catalogData?.substrate
          ? formatControlledVocabulary(catalogData.substrate as unknown[]) || ''
          : '',
        mediaDescription: (catalogData?.mediaDescription as string) || '',
        imageUrl: thumbnailUrl || undefined,
      };

      await generateCatalogPdf([pdfItem], { includePrivateFields }, onProgress);
      setSnackbarMessage('PDF exported successfully');
      setSnackbarOpen(true);
    } catch (err) {
      logger.error('PDF export failed', err);
      setSnackbarMessage('PDF export failed. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setPdfExporting(false);
    }
  };

  const handleRestoreConfirm = async () => {
    if (!artworkId) return;
    setRestoreLoading(true);
    setRestoreError(null);
    try {
      await restoreCatalogItem(artworkId);
      setSnackbarMessage('Item restored successfully');
      setRestoreDialogOpen(false);
      setSnackbarOpen(true);
      const data = await getCatalogItem(artworkId, activeCatalogOwnerId);
      if (data) setCatalogItemData(data as CatalogItem);
    } catch (err: unknown) {
      setRestoreError(extractAxiosErrorMessage(err, 'Operation failed'));
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleDeleteConfirm = async (password?: string) => {
    if (!artworkId) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await deleteCatalogItem(artworkId, activeCatalogOwnerId);
      setDeleteDialogOpen(false);
      navigate('/my-objects', { state: { snackbar: 'Object deleted successfully', deletedItemId: artworkId } });
    } catch (err: unknown) {
      setDeleteError(extractAxiosErrorMessage(err, 'Failed to delete object'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleClearDialogClose = () => {
    setClearDialogOpen(false);
    setClearError(null);
  };

  const handleClearConfirm = async () => {
    if (!artworkId) return;
    setClearLoading(true);
    setClearError(null);
    try {
      // Password re-auth was removed (it triggered an MFA/SMS code); the
      // user's session already authorizes the clear.
      await clearCatalogItem(artworkId, activeCatalogOwnerId);
      handleClearDialogClose();
      navigate('/my-objects', {
        state: { snackbar: 'Item cleared for reuse', clearedItemId: artworkId },
      });
    } catch (err: unknown) {
      logger.error('Failed to clear catalog item', { artworkId, ownerId: activeCatalogOwnerId, err });
      setClearError(extractAxiosErrorMessage(err, 'Failed to clear catalog item'));
    } finally {
      setClearLoading(false);
    }
  };

  // Create fake artwork data for delegate views
  const fakeArtwork = useMemo(() => {
    if (!artworkId || !isPublicRoute) return null;

    const [userId] = artworkId.split('-artwork-');
    if (!userId || (userId !== 'creator-001' && userId !== 'creator-002')) return null;

    const artworkNumber = artworkId.split('-artwork-')[1];
    const creatorName = userId === 'creator-001' ? 'John Artist' : 'Jane Sculptor';

    const artworkData = {
      '1': {
        title: 'Abstract Composition #1',
        workType: 'Painting',
        technique: 'Oil on canvas',
        materials: 'Canvas, Oil paint',
        creationDate: '2023',
        dimensions: '24" x 36"',
        condition: 'Excellent',
        keywords: ['abstract', 'contemporary', 'colorful'],
        notes: 'Part of the contemporary series',
        location: 'Studio A',
        estimatedValue: '$2,500'
      },
      '2': {
        title: 'Urban Landscape',
        workType: userId === 'creator-001' ? 'Painting' : 'Sculpture',
        technique: userId === 'creator-001' ? 'Acrylic on canvas' : 'Bronze casting',
        materials: userId === 'creator-001' ? 'Canvas, Acrylic paint' : 'Bronze, Patina',
        creationDate: '2023',
        dimensions: userId === 'creator-001' ? '18" x 24"' : '12" x 8" x 6"',
        condition: 'Good',
        keywords: ['urban', 'modern', 'architectural'],
        notes: 'Inspired by city architecture',
        location: 'Gallery B',
        estimatedValue: '$1,800'
      },
      '3': {
        title: 'Nature Study',
        workType: 'Drawing',
        technique: 'Charcoal on paper',
        materials: 'Paper, Charcoal',
        creationDate: '2024',
        dimensions: '12" x 16"',
        condition: 'Excellent',
        keywords: ['nature', 'study', 'botanical'],
        notes: 'Botanical study from life',
        location: 'Studio A',
        estimatedValue: '$800'
      }
    };

    const data = artworkData[artworkNumber as keyof typeof artworkData];
    if (!data) return null;

    return {
      artworkId: artworkId,
      userId: userId,
      creator: creatorName,
      visibility: 'public',
      status: 'COMPLETED',
      createdAt: '2023-01-15T10:00:00Z',
      updatedAt: '2023-01-15T10:00:00Z',
      images: [],
      ...data
    };
  }, [artworkId, isPublicRoute]);

  // Find the specific artwork by ID
  // For public routes: use fetched publicArtwork (or fallback to fakeArtwork for demo)
  // For private routes: use catalogItemData fetched via getCatalogItem (single-item endpoint)
  const currentArtwork = isPublicRoute
    ? (publicArtwork || fakeArtwork)
    : (catalogItemData as unknown as Artwork | undefined);

  // Check if current user can edit this artwork.
  // When viewing a delegated catalog, only 'full' permission allows editing.
  // When viewing own catalog, use standard role-based permission check.
  const isViewingDelegatedCatalog = Boolean(activeCatalogOwnerId);
  const canEditDelegatedCatalog = activeDelegation?.permissions === 'full';
  const canEdit = Boolean(
    currentArtwork?.userId &&
    artworkId &&
    (isViewingDelegatedCatalog
      ? canEditDelegatedCatalog
      : canEditCatalog(artworkId, currentArtwork.userId))
  );

  // Load catalog description data (pass ownerId for delegated access)
  const { loadDescription } = useCatalogDescriptions(artworkId, effectiveOwnerId);
  const { loadKeywords } = useCatalogKeywords(artworkId, effectiveOwnerId);
  const { loadMeasurements } = useCatalogMeasurements(artworkId, effectiveOwnerId);

  useEffect(() => {
    if (artworkId && !isPublicRoute) {
      let cancelled = false;
      const fetchData = async () => {
        try {
          const data = await loadDescription();
          if (!cancelled) {
            setCatalogData(data);
          }
        } catch (error) {
          if (!cancelled) {
            logger.error('Failed to load catalog data', error);
          }
        }
      };
      fetchData();
      return () => { cancelled = true; };
    }
  }, [artworkId, isPublicRoute, loadDescription]);

  // Load catalog keywords data (for ALI catalog number)
  useEffect(() => {
    if (artworkId && !isPublicRoute) {
      let cancelled = false;
      const fetchKeywords = async () => {
        try {
          const data = await loadKeywords();
          if (!cancelled && data) {
            setKeywordsData(data);
          }
        } catch (error) {
          if (!cancelled) {
            logger.error('Failed to load keywords data', error);
          }
        }
      };
      fetchKeywords();
      return () => { cancelled = true; };
    }
  }, [artworkId, isPublicRoute, loadKeywords]);

  // Load catalog measurements data (for dimensions in hero)
  // Authenticated-only: the /api/measurements endpoint requires auth.
  // On public routes without auth, calling it returns 401 which triggers
  // a redirect to /auth (via api.ts response interceptor), breaking the
  // private-artwork friendly message flow.
  useEffect(() => {
    if (artworkId && !isPublicRoute) {
      let cancelled = false;
      const fetchMeasurements = async () => {
        try {
          const data = await loadMeasurements();
          if (!cancelled && data) {
            setMeasurementData(data);
          }
        } catch (err) {
          if (!cancelled) {
            logger.error('Failed to load measurement data', err);
          }
        }
      };
      fetchMeasurements();
      return () => { cancelled = true; };
    }
  }, [artworkId, isPublicRoute, loadMeasurements]);

  // Load full catalog item data (primary source for authenticated routes)
  useEffect(() => {
    if (artworkId && !isPublicRoute) {
      let cancelled = false;
      const fetchCatalogItem = async () => {
        setItemLoading(true);
        setItemError(null);
        try {
          const data = await getCatalogItem(artworkId, effectiveOwnerId);
          if (!cancelled && data) {
            setCatalogItemData(data as CatalogItem);
          }
        } catch (err) {
          if (!cancelled) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load artwork';
            setItemError(errorMessage);
            logger.error('Failed to load catalog item data', err);
          }
        } finally {
          if (!cancelled) setItemLoading(false);
        }
      };
      fetchCatalogItem();
      return () => { cancelled = true; };
    }
  }, [artworkId, isPublicRoute, getCatalogItem, effectiveOwnerId]);

  // Load provenance and additional info data (authenticated routes only)
  useEffect(() => {
    if (artworkId && !isPublicRoute) {
      let cancelled = false;
      const fetchExtraData = async () => {
        try {
          const [provResponse, addlInfo] = await Promise.all([
            getProvenanceChain(artworkId, effectiveOwnerId).catch(() => ({ records: [], version: 0, chainLength: 0 })),
            getAdditionalInfo(artworkId, effectiveOwnerId).catch(() => null),
          ]);
          if (!cancelled) {
            setProvenanceData((provResponse.records ?? []) as Array<Record<string, unknown>>);
            if (addlInfo) setAdditionalInfoData(addlInfo as Record<string, unknown>);
          }
        } catch (err) {
          if (!cancelled) {
            logger.error('Failed to load provenance/additional info', err);
          }
        }
      };
      fetchExtraData();
      return () => { cancelled = true; };
    }
  }, [artworkId, isPublicRoute, getProvenanceChain, getAdditionalInfo, effectiveOwnerId]);

  // Multi-image support via useObjectImages hook
  const { images: objectImages } = useObjectImages(
    artworkId || '',
    catalogItemData as CatalogItemData | null
  );

  // On public routes, derive catalogData and keywordsData from publicArtwork
  // since the separate authenticated endpoints (/api/descriptions, /api/keywords) are not available
  useEffect(() => {
    if (isPublicRoute && publicArtwork) {
      setCatalogData({
        workType: publicArtwork.workType,
        techniques: publicArtwork.techniques,
        materials: publicArtwork.materials,
        signatures: publicArtwork.signatures,
        substrate: publicArtwork.substrate,
        creationDate: publicArtwork.creationDate,
        mediaDescription: publicArtwork.mediaDescription,
        mediaAdditionalComments: publicArtwork.mediaAdditionalComments,
        // Public API returns creators as `creatorsList` (array of {name} objects).
        // Map it to the same `creators` key used by the authenticated descriptions
        // endpoint so the hero artist block has a single read path.
        creators: publicArtwork.creatorsList as HeroCatalogData['creators'],
      });
      setKeywordsData({
        aliCatalogNumber: publicArtwork.aliCatalogNumber,
      });
      setMeasurementData({
        dimensions: publicArtwork.dimensions || [],
        unit: publicArtwork.unit || 'in',
      } as MeasurementData);
      setAdditionalInfoData({
        publicNotes: publicArtwork.publicNotes || null,
        physicalLocation: publicArtwork.physicalLocation || null,
        relatedWorks: publicArtwork.relatedWorks || [],
        exhibitions: publicArtwork.exhibitions || [],
      });
      setProvenanceData((publicArtwork.provenance || []) as Array<Record<string, unknown>>);
    }
  }, [isPublicRoute, publicArtwork]);

  // Parse creation date for hero and additional info usage
  const parsedCreationDate = (() => {
    if (!catalogData?.creationDate) return null;
    if (typeof catalogData.creationDate === 'string' && (catalogData.creationDate as string).includes('{')) {
      try {
        return JSON.parse(catalogData.creationDate as string);
      } catch (error) {
        logger.debug('Failed to parse creation date JSON', error);
        return catalogData.creationDate;
      }
    }
    return catalogData.creationDate;
  })();

  // Local override: includes non-linear dimensions (weight, etc.) beyond shared formatHeroDimensions
  const formatHeroDimensionsLocal = (data: MeasurementData | null): string | null => {
    if (!data?.dimensions || data.dimensions.length === 0) return null;

    const linearParts = ['height', 'width', 'depth']
      .map(type => {
        const dim = data.dimensions.find(d => d.type.toLowerCase() === type);
        if (!dim?.value) return null;
        const value = dim.fraction ? `${dim.value} ${dim.fraction}` : dim.value;
        const dimUnit = dim.unit || data.unit;
        return `${value} ${dimUnit} (${DIMENSION_LABELS[type]})`;
      })
      .filter(Boolean);
    const linearStr = linearParts.length > 0 ? linearParts.join(' x ') : null;

    const nonLinearDims = data.dimensions
      .filter(d => !LINEAR_DIMENSION_TYPES.has(d.type.toLowerCase()) && d.value)
      .map(d => {
        const val = d.fraction ? `${d.value} ${d.fraction}` : d.value;
        const dimUnit = d.unit || data.unit;
        return `${val} ${dimUnit}`;
      });

    const allParts = [linearStr, ...nonLinearDims].filter(Boolean);
    return allParts.length > 0 ? allParts.join('; ') : null;
  };

  // Format medium (techniques + materials) for hero display, preferring free-text
  const formatMedium = (): string | null => {
    return formatMediumHelper(
      catalogData?.techniques as unknown[] | undefined,
      catalogData?.materials as unknown[] | undefined,
      catalogData?.mediaDescription as string | undefined
    );
  };

  // Helper function to safely format date
  const formatDate = (dateValue: unknown): string => {
    if (!dateValue) return 'N/A';

    try {
      // If it's already a string in a readable format, return it
      if (typeof dateValue === 'string') {
        // Try to parse it as a date
        const date = new Date(dateValue);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
        }
        // If it looks like a JSON string, try to parse it
        if (dateValue.includes('{') || dateValue.includes('[')) {
          try {
            const parsed = JSON.parse(dateValue);
            return formatDate(parsed);
          } catch (error) {
            logger.debug('Failed to parse date JSON string', error);
            return dateValue;
          }
        }
        return dateValue;
      }

      // If it's a Date object
      if (dateValue instanceof Date) {
        return dateValue.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }

      // If it's an object with a date property
      if (typeof dateValue === 'object') {
        const possibleDateValue = dateValue.value || dateValue.date || dateValue.display;
        if (possibleDateValue) {
          return formatDate(possibleDateValue);
        }
      }

      return String(dateValue);
    } catch (error) {
      logger.error('Error formatting date', error, dateValue);
      return String(dateValue);
    }
  };

  const handleBackToCatalog = () => {
    const navState = location.state as { browsePage?: number; from?: string } | null;

    // When the user arrived from the search page, use browser history to return
    // there (preserving the search query in the URL). Regression fix for #1181.
    if (navState?.from === 'search') {
      navigate(-1);
      return;
    }

    if (isPublicRoute) {
      const ownerAliId = currentArtwork?.aliCatalogNumber?.split('.')[0];
      navigate(ownerAliId ? `/public/catalog/${ownerAliId}` : '/public/catalog');
    } else {
      // resetCollection: true tells MyObjects to clear any active album/collection
      // filter on arrival. Without this, the collection selected before opening
      // the detail page persists on the catalog view and only the album's items
      // are shown instead of the full catalog (issue #1159).
      navigate('/my-objects', { state: { browsePage: navState?.browsePage, resetCollection: true } });
    }
  };

  // Show loading state for both private (getCatalogItem) and public (fetch) routes
  const isLoading = isPublicRoute ? publicLoading : itemLoading;
  const loadError = isPublicRoute ? publicError : itemError;

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (isPublicRoute && isPrivateArtwork) {
    // Recovery link carries ``?owner=<ownerId>`` when the backend told us who
    // owns this private artwork — without it the authenticated route would
    // re-fetch as the caller and 404 again. Disabled when ownerId is unknown
    // (older backend / non-delegate caller).
    const checkAccessTo = privateArtworkOwnerId
      ? `/object-details/${artworkId}?owner=${encodeURIComponent(privateArtworkOwnerId)}`
      : null;
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          This artwork is private and not available for public viewing.
        </Alert>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isAuthenticated && artworkId && checkAccessTo && (
            <Button variant="outlined" component={Link} to={checkAccessTo}>
              Check if you have access
            </Button>
          )}
          <Button variant="text" onClick={() => navigate(-1)}>
            Go back
          </Button>
        </Box>
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Error loading artwork: {loadError}</Alert>
      </Container>
    );
  }

  if (!currentArtwork) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">Artwork not found</Alert>
      </Container>
    );
  }

  const artworkRecord = currentArtwork as Record<string, unknown>;
  // On public routes, do NOT fall back to getArtworkTitle() which uses original.filename as a
  // title fallback. The filename could reveal a private title (e.g., "Red Muse.jpg"). When the
  // backend strips the title field because it is private, we must show 'Untitled' rather than
  // leaking the title via the filename. Regression fix for #901.
  const title = currentArtwork.status === 'CLEARED'
    ? 'Available for Reuse'
    : isPublicRoute
      ? ((artworkRecord.title as string) || 'Untitled')
      : ((artworkRecord.title as string) || getArtworkTitle(currentArtwork) || 'Untitled');
  const thumbnailUrl = getThumbnailUrl(currentArtwork);
  // IP protection: public viewers get the medium (2048 px) rendition; authenticated
  // owners and delegates get the full large (4096 px) rendition. The backend public
  // endpoint also strips 'large' from its responses, so this is defense in depth.
  const detailImageUrl = isPublicRoute
    ? getPublicDetailImageUrl(currentArtwork)
    : getDetailImageUrl(currentArtwork);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 1.5, sm: 3 } }}>
      {/* Public View Banner - shown when navigating from catalog with ?from=upload */}
      {showPublicViewBanner && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
          }
        >
          This content about your object is: <strong>Public</strong>
        </Alert>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={handleBackToCatalog}
            aria-label="Back to catalog"
            sx={{ mr: 1 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Catalog</Typography>
          {browseNav.positionLabel && (
            <BrowseNavigationBar
              canPrev={browseNav.canPrev}
              canNext={browseNav.canNext}
              loading={browseNav.isFetching}
              positionLabel={browseNav.positionLabel}
              onPrev={browseNav.onPrev}
              onNext={browseNav.onNext}
            />
          )}
        </Box>
        {!isPublicRoute && (
          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 2 } }}>
            {isMobile ? (
              <>
                <Tooltip title={en.catalogItems.addToCatalog}>
                  <IconButton
                    aria-label="Add to Album"
                    onClick={() => setAddToCollectionDialog(true)}
                  >
                    <FolderOpen />
                  </IconButton>
                </Tooltip>
                {canEdit && (
                  <Tooltip title="Edit Object">
                    <IconButton
                      aria-label="Edit Object"
                      color="primary"
                      onClick={() => navigate(`/upload-object?edit=${artworkId}${activeCatalogOwnerId ? `&owner=${activeCatalogOwnerId}` : ''}`, { state: { from: 'object-details' } })}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}
                {canEdit && (catalogItemData?.isLastCatalogItem ?? true) && (
                  <Tooltip title="Delete Object">
                    <IconButton
                      aria-label="Delete Object"
                      color="error"
                      onClick={() => setDeleteDialogOpen(true)}
                      data-testid="delete-object-button"
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
                {canEdit && catalogItemData && catalogItemData.isLastCatalogItem === false && (
                  <Tooltip title="Clear for Reuse">
                    <IconButton
                      aria-label="Clear for Reuse"
                      color="warning"
                      onClick={() => setClearDialogOpen(true)}
                      data-testid="clear-for-reuse-button"
                    >
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Export">
                  <IconButton
                    aria-label="Export"
                    onClick={() => setExportDialogOpen(true)}
                  >
                    <PictureAsPdf />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Share">
                  <IconButton aria-label="Share" onClick={handleShare}>
                    <Share />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<FolderOpen />}
                  onClick={() => setAddToCollectionDialog(true)}
                >
                  {en.catalogItems.addToCatalog}
                </Button>
                {canEdit && (
                  <Button
                    variant="contained"
                    startIcon={<Edit sx={{ color: 'white' }} />}
                    onClick={() => navigate(`/upload-object?edit=${artworkId}${activeCatalogOwnerId ? `&owner=${activeCatalogOwnerId}` : ''}`, { state: { from: 'object-details' } })}
                  >
                    Edit Object
                  </Button>
                )}
                {canEdit && (catalogItemData?.isLastCatalogItem ?? true) && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => setDeleteDialogOpen(true)}
                    data-testid="delete-object-button"
                  >
                    Delete
                  </Button>
                )}
                {canEdit && catalogItemData && catalogItemData.isLastCatalogItem === false && (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<ClearIcon />}
                    onClick={() => setClearDialogOpen(true)}
                    data-testid="clear-for-reuse-button"
                  >
                    Clear for Reuse
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<PictureAsPdf />}
                  onClick={() => setExportDialogOpen(true)}
                >
                  Export
                </Button>
                <Button variant="outlined" startIcon={<Share />} onClick={handleShare}>
                  Share
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* Destroyed Status Alert */}
      {currentArtwork.status === 'DESTROYED' && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            canEdit ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => { setRestoreError(null); setRestoreDialogOpen(true); }}
              >
                Restore Item
              </Button>
            ) : undefined
          }
        >
          This catalog item has been marked as destroyed.
        </Alert>
      )}

      {/* Cleared Status Alert */}
      {currentArtwork.status === 'CLEARED' && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          action={
            canEdit ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate(`/upload-object?reuseId=${currentArtwork.artworkId}`)}
              >
                Upload New Images
              </Button>
            ) : undefined
          }
        >
          This catalog slot has been cleared and is available for reuse.
        </Alert>
      )}

      {/* Deleted Status Alert */}
      {currentArtwork.status === 'DELETED' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This catalog item has been deleted. The catalog number is preserved.
        </Alert>
      )}

      {/* Duplicate Status Alert */}
      {currentArtwork.status === 'DUPLICATE' && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            currentArtwork.duplicateOfCatalogItemId ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate(`/object-details/${currentArtwork.duplicateOfCatalogItemId}`)}
              >
                View Original
              </Button>
            ) : undefined
          }
        >
          This item is a duplicate of {currentArtwork.duplicateOfCatalogNumber || 'another item'}.
        </Alert>
      )}

      {/* Top Section - Image and Basic Info */}
      <Box sx={{ display: 'flex', gap: 2.5, mb: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Image */}
        <Box sx={{ flex: 1 }}>
          {currentArtwork.status === 'CLEARED' ? (
            <Box sx={{ width: '100%', height: isMobile ? 250 : 400 }}>
              <ReusePlaceholder variant="detail" />
            </Box>
          ) : currentArtwork.status === 'DELETED' ? (
            <Box sx={{ width: '100%', height: isMobile ? 250 : 400 }}>
              <DeletedPlaceholder variant="detail" />
            </Box>
          ) : objectImages.length > 1 ? (
            <ImageGallery
              images={objectImages.map(img => ({
                imageId: img.imageId,
                url: img.displayUrl || img.url || '',
                thumbnailUrl: img.thumbnailUrl,
                visibility: img.visibility,
                filename: img.filename,
                uploadedAt: img.uploadedAt,
              }))}
              title={title}
              canEdit={canEdit}
            />
          ) : (detailImageUrl || thumbnailUrl) ? (
            <Box
              component="img"
              crossOrigin={getCrossOrigin(detailImageUrl || thumbnailUrl!)}
              src={detailImageUrl || thumbnailUrl!}
              alt={title}
              sx={{
                width: '100%',
                maxHeight: isMobile ? 350 : 600,
                objectFit: 'contain',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1
              }}
            />
          ) : (
            <Box sx={{ width: '100%', height: isMobile ? 250 : 400 }}>
              <NoImagePlaceholder variant="detail" />
            </Box>
          )}
        </Box>

        {/* Basic Info - RL Catalog Style Vertical Stack */}
        <Box sx={{ flex: 1 }}>
          {/* Artist — displayed above the title.
              Primary source: catalogData.creators (from /api/descriptions or mapped
              from publicArtwork.creatorsList on public routes) gives the full list
              including co-creators. Falls back to the single-string creator extracted
              by _build_catalog_response on the catalog-item endpoint for legacy/new
              entries where descriptions haven't been saved yet. No "Unknown Artist"
              placeholder on own-catalog authenticated pages (fix for #1157 / #1169). */}
          {(() => {
            // Creator names, owner first. Prefer the descriptions / public
            // creators list; fall back to the single-string creator from the
            // catalog-item endpoint for legacy entries (the sole owner-creator).
            const creatorNames = getCreatorNames(catalogData?.creators);
            const fallbackCreator =
              (currentArtwork.creator as string | undefined)?.trim() || null;
            const names =
              creatorNames.length > 0
                ? creatorNames
                : fallbackCreator
                  ? [fallbackCreator]
                  : [];
            if (names.length === 0) return null;
            const [ownerName, ...coCreators] = names;
            // On public routes, link ONLY the owner name (first creator) to their
            // public catalog. The owner's ALI ID is the prefix of the catalog
            // number ("JD-3K7.0001" → "JD-3K7"); co-creators have no addressable
            // catalog. Validate the LL-NXX shape so a missing/malformed number
            // degrades to plain text instead of a broken link. GH#1182.
            const catalogNumberHead = isPublicRoute
              ? (currentArtwork.aliCatalogNumber as string | undefined)?.split('.')[0]?.trim()
              : undefined;
            const ownerAliIdForLink =
              catalogNumberHead && /^[A-Z]{2}-[A-Z0-9]{3}$/.test(catalogNumberHead)
                ? catalogNumberHead
                : null;
            const ownerContent = ownerAliIdForLink ? (
              <Link
                to={`/public/catalog/${ownerAliIdForLink}`}
                aria-label={`View ${ownerName}'s public catalog`}
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                {ownerName}
              </Link>
            ) : (
              ownerName
            );
            return (
              <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5, fontWeight: 400, fontSize: { xs: '1rem', sm: '1.25rem' } }} data-testid="baseball-card-artist">
                {ownerContent}
                {coCreators.length > 0 ? `, ${coCreators.join(', ')}` : ''}
              </Typography>
            );
          })()}

          {/* Title */}
          <Typography variant="h4" sx={{ mb: 0.75, fontStyle: 'italic', lineHeight: 1.2, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            {title}
          </Typography>

          {/* Date — hero/baseball-card header shows year-only (or start–end
              year range), matching the thumbnail grid. The full entered date
              is shown in the Description accordion below (GH#1229). */}
          {(() => {
            const dateStr = formatCreationDateThumbnail(parsedCreationDate);
            if (!dateStr) return null;
            return (
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontWeight: 400, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {dateStr}
              </Typography>
            );
          })()}

          {/* Work Type — small caps under creator */}
          {(() => {
            const workType = catalogData?.workType
              ? formatSingleControlledVocabulary(catalogData.workType)
              : null;
            if (!workType) return null;
            return (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.8, mb: 0.75 }}
              >
                {workType}
              </Typography>
            );
          })()}

          {/* Dimensions — label-less value, positioned above identifiers */}
          {(() => {
            const dims = formatHeroDimensionsLocal(measurementData);
            if (!dims) return null;
            return (
              <Typography variant="body1" sx={catalogValueSx}>
                {dims}
              </Typography>
            );
          })()}

          {/* ALI Catalog Number */}
          <Box sx={{ mt: 0.75, mb: 1.25 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', letterSpacing: '0.05em' }}
            >
              {keywordsData?.aliCatalogNumber || 'Not assigned'}
            </Typography>

            {/* Inventory Numbers */}
            {keywordsData?.customCatalogNumbers && (keywordsData.customCatalogNumbers as string[]).length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', letterSpacing: '0.05em', fontSize: '0.85rem', opacity: 0.8 }}
                data-testid="custom-catalog-numbers"
              >
                {(keywordsData.customCatalogNumbers as string[]).join(', ')}
              </Typography>
            )}
          </Box>

          {/* Media — label-less value */}
          {(() => {
            const medium = formatMedium();
            if (!medium) return null;
            return (
              <Typography variant="body1" sx={catalogValueSx}>
                {medium}
              </Typography>
            );
          })()}

          {/* Inscriptions — label-less flattened signature data */}
          {(() => {
            if (!catalogData?.signatures) return null;
            let parsed: unknown[];
            try {
              const raw = typeof catalogData.signatures === 'string'
                ? JSON.parse(catalogData.signatures as string)
                : catalogData.signatures;
              parsed = Array.isArray(raw) ? raw : [];
            } catch {
              parsed = [];
            }
            const inscriptionStr = formatInscriptions(parsed);
            if (!inscriptionStr) return null;
            return (
              <Typography variant="body1" sx={catalogValueSx}>
                {inscriptionStr}
              </Typography>
            );
          })()}

          {/* Editions */}
          {(() => {
            const editions = catalogData?.editions as Array<{ type: string; count: number; total: number }> | undefined;
            if (!editions || editions.length === 0) return null;
            return (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {editions.map((e) => `${e.type}: ${e.count}/${e.total}`).join('; ')}
              </Typography>
            );
          })()}
        </Box>
      </Box>

      {/* Redesigned Sections */}
      <Box sx={{ mt: 3 }}>
        <DescriptionSection
          artworkId={artworkId || ''}
          title={title}
          catalogData={catalogData}
          keywordsData={keywordsData}
          measurementData={measurementData}
          additionalInfoData={additionalInfoData}
          parsedCreationDate={parsedCreationDate}
          isOwnerView={!isPublicRoute && !effectiveOwnerId}
        />

        <ExhibitionsPublicationsSection additionalInfoData={additionalInfoData} />

        <AwardsSection additionalInfoData={additionalInfoData} />

        <ProvenanceSection
          provenanceData={provenanceData}
          isPublicRoute={isPublicRoute}
        />

        {!isPublicRoute && (
          <InternalOnlyDataSection additionalInfoData={additionalInfoData} />
        )}

        {!isPublicRoute && (
          <DocumentsSection
            catalogId={artworkId ?? null}
            ownerId={activeCatalogOwnerId ?? undefined}
            readOnly
          />
        )}

        <RelatedItemsSection
          additionalInfoData={additionalInfoData}
          ownerId={activeCatalogOwnerId}
          isPublicRoute={isPublicRoute}
        />

        <Box sx={{ mt: 2, px: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Registered:</strong> {formatDate(currentArtwork.createdAt)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Last Updated:</strong> {formatDate(currentArtwork.updatedAt)}
          </Typography>
        </Box>
      </Box>

      {/* Share Modal */}
      <Dialog open={shareModalOpen} onClose={() => setShareModalOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ position: 'relative', pr: 5 }}>
          Share Object
          <IconButton onClick={() => setShareModalOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Share Link"
            value={shareUrl}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton onClick={handleCopyLink}>
                  <ContentCopy />
                </IconButton>
              ),
            }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add to Collection Dialog */}
      <AddToCollectionDialog
        open={addToCollectionDialog}
        itemId={artworkId || ''}
        itemTitle={title}
        onClose={() => setAddToCollectionDialog(false)}
        onSuccess={() => {
          setSnackbarMessage(en.catalogItems.addSuccess);
          setSnackbarOpen(true);
        }}
      />

      {/* Export Dialog */}
      {/* Pass ownerId and selectedIds so delegate CSV/image exports are scoped
          to the owner's specific item (GH#1176). Uses effectiveOwnerId (?owner=
          param ∥ activeCatalogOwnerId) so the export works whether the delegate
          arrived via the sidebar catalog switch OR a ?owner= link. The PDF path
          is local (no API call) so it is unaffected by these props, but passing
          them is harmless and keeps the prop surface consistent with MyObjects.
          The backend (resolve_effective_user_id) still verifies the delegation,
          so a non-delegate cannot export another owner's items via ownerId. */}
      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        itemCount={1}
        onExportPdf={handleExportPdf}
        ownerId={effectiveOwnerId || undefined}
        selectedIds={artworkId ? [artworkId] : undefined}
      />

      {/* Clear for Reuse Dialog */}
      <Dialog
        open={clearDialogOpen}
        onClose={handleClearDialogClose}
      >
        <DialogTitle>Clear Catalog Entry?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove all data and images from this catalog entry.
            The catalog number ({keywordsData?.aliCatalogNumber || ''}) will be preserved and available
            for reuse with new content.
          </DialogContentText>
          {clearError && <Alert severity="error" sx={{ mt: 2 }}>{clearError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClearDialogClose}
            disabled={clearLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClearConfirm}
            color="warning"
            variant="contained"
            disabled={clearLoading}
          >
            {clearLoading ? 'Clearing...' : 'Clear Entry'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Object Dialog */}
      <DeleteObjectDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setDeleteError(null); }}
        onConfirm={handleDeleteConfirm}
        itemCount={1}
        itemName={catalogItemData ? getArtworkTitle(catalogItemData) : publicArtwork?.title}
        loading={deleteLoading}
        error={deleteError}
      />

      {/* Restore Dialog */}
      <MarkDestroyedDialog
        open={restoreDialogOpen}
        mode="restore"
        onClose={() => setRestoreDialogOpen(false)}
        onConfirm={handleRestoreConfirm}
        loading={restoreLoading}
        error={restoreError}
      />

      {/* Snackbar for copy confirmation */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
};
